import { notFound } from 'next/navigation'

import { getCustomerAnalyticsUrl } from '~/src/components/RyderShare/UsagePage/helpers'

import {
  ryderShareAuthenticatedPages,
  operationalAuthenticatedPages,
  sysadminAuthenticatedPages,
  tmCustomerAuthenticatedPages,
  DEFAULT_OPERATOR_HOME_PAGE,
  integratedCustomerAuthenticatedPages,
  NETWORK,
  USAGE,
} from '~/app/constants'

import { CustomerType, HomePage, UserRole } from '~/__generated_types__/globalTypes'

import { Page } from '~/app/types'

const getOperatorPages = (role: UserRole): Array<Page> => {
  if (role === UserRole.Sysadmin) {
    return [...operationalAuthenticatedPages, ...sysadminAuthenticatedPages]
  }

  return [...operationalAuthenticatedPages]
}

const getCustomerPages = (
  role: UserRole,
  customerType: CustomerType | null | undefined
): Array<Page> => {
  if (role === UserRole.Customer) {
    if (customerType === CustomerType.Integrated) {
      return [...integratedCustomerAuthenticatedPages]
    }

    if (customerType === CustomerType.Tm) {
      return [...tmCustomerAuthenticatedPages]
    }

    if (customerType === CustomerType.Dts) {
      return [...ryderShareAuthenticatedPages]
    }
  }

  return []
}

const getAvailablePages = (
  role: UserRole,
  customerType: CustomerType | null | undefined
): Array<Page> => {
  switch (role) {
    case UserRole.Operator:
      return [...operationalAuthenticatedPages]
    case UserRole.Customer:
      if (customerType === CustomerType.Integrated) {
        return [...integratedCustomerAuthenticatedPages]
      }

      if (customerType === CustomerType.Tm) {
        return [...tmCustomerAuthenticatedPages]
      }

      if (customerType === CustomerType.Dts) {
        return [...ryderShareAuthenticatedPages]
      }

      return []
    case UserRole.Sysadmin:
      return [...operationalAuthenticatedPages, ...sysadminAuthenticatedPages]
    default:
      return []
  }
}

const isCustomerPageVisible = (
  page: Page,
  {
    flags,
    customerName,
    hasSecureFilters,
  }: {
    flags: Record<string, unknown>
    customerName?: string | null
    hasSecureFilters: boolean
  }
): boolean => {
  if (hasSecureFilters && (page.key === NETWORK || page.key === USAGE)) {
    return false
  }

  if (page.featureFlag && !flags[page.featureFlag]) {
    return false
  }

  if (page.key === USAGE && !getCustomerAnalyticsUrl(customerName)) {
    return false
  }

  return true
}

const getCurrentPage = (pathname: string, availablePages: Array<Page>): Page | null => {
  return (
    availablePages.find((page) => {
      const pathPattern = page.path.replace(/:\w+/g, '\\d+')
      const regex = new RegExp(`^${pathPattern}$`)

      if (regex.test(pathname) && page.isNavigable) {
        return page
      }
    }) ?? null
  )
}

const getOperatorHomePagePath = (homePage?: HomePage | null): string => {
  const page = homePage ?? DEFAULT_OPERATOR_HOME_PAGE

  switch (page) {
    case HomePage.DriverVisibility:
      return '/visibility'
    case HomePage.Loadboard:
      return '/all-loads'
    default:
      notFound()
  }
}

export {
  getAvailablePages,
  getCurrentPage,
  getOperatorHomePagePath,
  getOperatorPages,
  getCustomerPages,
  isCustomerPageVisible,
}
